import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Col, Form, FormGroup, Row } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import Immutable from 'immutable';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import DynamicForm from '../../../common/forms/DynamicForm';
import { BUS_MAINTENANCE_STAY_FORM_COLUMNS } from '../../../../config/dynamicFormFields';
import { getItineraryArrivals } from '../../../../actions';
import { isRequired } from '../../../../utils/validators';
import FormItem from '../../../common/forms/FormItem';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT, INT_DATE_TIME_FORMAT } from '../../../../config/locale';
import { ITINERARY_DIRECTION, ITINERARY_SEARCH_DIRECTION_OPTIONS } from '../../../../config/constants';
import DatePicker from '../../../common/forms/input/DatePicker';
import Select from '../../../common/forms/select/Select';

const validateDate = (value, allValues) => {
  if (allValues.fromDate && value < allValues.fromDate) {
    return 'La fecha de fin no puede ser anterior a la fecha de inicio.';
  }
  return undefined;
};

const selector = formValueSelector('NewBusMaintenanceStayForm');

export const NewBusMaintenanceStayForm = ({
  handleSubmit,
  loadingItineraryArrival,
  dispatchGetItineraryArrivals,
  itineraryArrivals,
  dispatchChange,
  loading,
  fromDateValue,
  toDateValue,
  itineraryDirectionValue
}) => {
  const onClickRemove = (index, fields) => {
    fields.remove(index);
  };

  const onClickAdd = (fields) => {
    fields.push({ select: true });
  };

  const handleItineraryArrivalSearch = () => {
    if (!itineraryDirectionValue) {
      toastr.warning(
        'Atención',
        'Debe seleccionar una ciudad para continuar.'
      );
      return;
    }

    const { value } = itineraryDirectionValue;

    if (!fromDateValue || !toDateValue || !value) return

    const fromDateString = tzNormalizeDate({ date: fromDateValue, format: INT_DATE_FORMAT });
    const toDateString = tzNormalizeDate({ date: toDateValue, format: INT_DATE_FORMAT });

    dispatchGetItineraryArrivals({
      itineraryDirection: value,
      fromDateString,
      toDateString,
    });
  };

  const getInitialValues = (itineraryArrivalsList) => ({
    busMaintenanceStayBuses: itineraryArrivalsList.map((arrival) => ({
      registeredBusId: {
        value: arrival.registeredBusId,
        label: arrival.companyBusId,
      },
      originCityId: {
        value: arrival.originCityId,
        label: arrival.originCityName,
      },
      destinationCityId: {
        value: arrival.destinationCityId,
        label: arrival.destinationCityName
      },
      estimatedArrivalDate: tzNormalizeDate({
        date: arrival.estimatedArrivalTime,
        format: INT_DATE_TIME_FORMAT
      }),
      select: false,
      itineraryId: arrival.itineraryId
    }))
  });

  useEffect(() => {
    if (itineraryArrivals.size === 0) {
      dispatchChange('NewBusMaintenanceStayForm', 'busMaintenanceStayBuses', []);
    } else {
      const itineraryArrivalsList = itineraryArrivals.toJS() || [];
      const initialValues = getInitialValues(itineraryArrivalsList);
      dispatchChange(
        'NewBusMaintenanceStayForm',
        'busMaintenanceStayBuses',
        initialValues.busMaintenanceStayBuses
      );
    }
  }, [itineraryArrivals]);

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup>
        <FormItem label='Seleccione Itinerario y Fechas'
          required>
          <Row>
            <Col>
              <Field
                name="itineraryDirection"
                component={Select}
                placeholder="Seleccione un itinerario"
                options={ITINERARY_SEARCH_DIRECTION_OPTIONS}
                validate={[isRequired]}
              />
            </Col>
            <Col>
              <Field
                name="fromDate"
                component={DatePicker}
                placeholder="Fecha de Inicio"
                validate={[isRequired, validateDate]}
              />
            </Col>
            <Col>
              <Field
                name="toDate"
                component={DatePicker}
                placeholder="Fecha de Fin"
                validate={[isRequired]}
              />
            </Col>
            <Col>
              <Button
                type="button"
                color="primary"
                onClick={handleItineraryArrivalSearch}
                disabled={loadingItineraryArrival}
              >
                Buscar Itinerarios
              </Button>
            </Col>
          </Row>
        </FormItem>
      </FormGroup>
      {loadingItineraryArrival ? (
        <Loader />
      ) : (
        <div className='pl-3'>
          <h2>Itinerarios</h2>
          <DynamicForm
            name="busMaintenanceStayBuses"
            columns={BUS_MAINTENANCE_STAY_FORM_COLUMNS}
            onClickRemove={onClickRemove}
            onClickAdd={onClickAdd}
            showRemoveButton={false}
          />
        </div>
      )}
      <FormFooter
        saveButtonDisabled={loading}
      />
    </Form>
  );
};

NewBusMaintenanceStayForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loadingItineraryArrival: PropTypes.bool,
  dispatchGetItineraryArrivals: PropTypes.func.isRequired,
  itineraryArrivals: PropTypes.instanceOf(Immutable.List).isRequired,
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fromDateValue: PropTypes.instanceOf(Date).isRequired,
  toDateValue: PropTypes.instanceOf(Date).isRequired,
  itineraryDirectionValue: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
};

NewBusMaintenanceStayForm.defaultProps = {
  loadingItineraryArrival: false,
  loading: false,
  itineraryDirectionValue: null,
};

const mapStateToProps = (state) => ({
  itineraryArrivals: state.MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'itineraryArrivals',
    'content',
  ]),
  loadingItineraryArrival: !state.MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'itineraryArrivals',
    'activity',
  ]).isEmpty(),
  loading: !state.MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  fromDateValue: selector(state, 'fromDate'),
  toDateValue: selector(state, 'toDate'),
  itineraryDirectionValue: selector(state, 'itineraryDirection'),
});

const mapDispatchToProps = {
  dispatchGetItineraryArrivals: getItineraryArrivals,
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: 'NewBusMaintenanceStayForm',
  enableReinitialize: false,
  keepDirtyOnReinitialize: true,
  initialValues: {
    fromDate: tzNormalizeDate(),
    toDate: tzNormalizeDate(),
    itineraryDirection: ITINERARY_DIRECTION.PROVINCE_TO_LIMA
  }
})(NewBusMaintenanceStayForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);